import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer, toast } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ModalGateProvider, useModalBlocker } from './contexts/ModalGateContext';
import { ThemeProvider } from 'styled-components';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';

import GlobalStyles from './styles/GlobalStyles';
import theme from './styles/theme';
import {
  listenForegroundMessages,
  handlePushAfterAuth,
  syncPushDeviceWithBackend,
  setPushPromptStatus,
  isWebPushConfigured,
  registerMessagingServiceWorker,
  requestNotificationPermissionNow,
} from './services/firebaseMessaging';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import HomologationModal from './components/HomologationModal';
import PushOptInModal from './components/PushOptInModal';
import AvisoHost from './components/AvisoHost';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Contrato from './pages/Contrato';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Dependents from './pages/Dependents';
import Payments from './pages/Payments';
import Coverage from './pages/Coverage';
import MaintenancePage from './pages/MaintenancePage';
import Solicitacoes from './pages/Solicitacoes';

// Styles
import 'react-toastify/dist/ReactToastify.css';

// Configuração do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Componente de rota protegida
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Componente de rota pública (redireciona se já autenticado)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // loading aqui é só checagem inicial de sessão.
  // O login NÃO altera loading (senão esta rota desmontava o Login e o modal sumia).
  if (loading) {
    return <LoadingSpinner />;
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

// Layout principal da aplicação
const AppLayout = ({ children }) => {
  const { isAuthenticated, selectedContrato } = useAuth();
  const [showPushOptIn, setShowPushOptIn] = useState(false);
  const [pushPromptPending, setPushPromptPending] = useState(false);

  useModalBlocker('push-opt-in-pending', pushPromptPending);

  // Web Push: SW + foreground + sync/opt-in após auth
  useEffect(() => {
    if (!isAuthenticated) {
      setShowPushOptIn(false);
      setPushPromptPending(false);
      return undefined;
    }

    let cancelled = false;
    let unsubscribe = () => {};
    let promptTimer = null;

    (async () => {
      try {
        if (isWebPushConfigured()) {
          await registerMessagingServiceWorker();
        }
      } catch (err) {
        console.warn('Falha ao registrar service worker FCM:', err);
      }

      if (cancelled) return;

      try {
        const contrato = selectedContrato?.numero;
        const outcome = await handlePushAfterAuth({ contrato });
        if (!cancelled && outcome.action === 'show_prompt') {
          // Segura outros modais durante a espera e abre o push após 5s
          setPushPromptPending(true);
          promptTimer = setTimeout(() => {
            if (!cancelled) setShowPushOptIn(true);
          }, 5000);
        }
      } catch (err) {
        console.warn('Falha no fluxo de push pós-auth:', err);
      }

      if (cancelled) return;

      const unsub = await listenForegroundMessages((payload) => {
        const title = payload?.notification?.title || 'Santa Casa Copacabana';
        const body = payload?.notification?.body || '';
        toast.info(body ? `${title}: ${body}` : title);
      });

      if (cancelled) {
        unsub();
        return;
      }
      unsubscribe = unsub;
    })();

    return () => {
      cancelled = true;
      if (promptTimer) clearTimeout(promptTimer);
      unsubscribe();
      setPushPromptPending(false);
      setShowPushOptIn(false);
    };
  }, [isAuthenticated, selectedContrato?.numero]);

  const handlePushAccept = async () => {
    // Obrigatório no mesmo clique — sem await antes, senão o Chrome bloqueia o prompt
    const permission = await requestNotificationPermissionNow();
    if (permission !== 'granted') {
      setPushPromptStatus('dismissed');
      setPushPromptPending(false);
      setShowPushOptIn(false);
      toast.warn(
        permission === 'denied'
          ? 'O navegador bloqueou as notificações. Clique no cadeado ao lado da URL e permita notificações, depois ative em Perfil.'
          : 'Não foi possível obter permissão de notificação.'
      );
      return;
    }

    const result = await syncPushDeviceWithBackend({
      contrato: selectedContrato?.numero,
      permission: 'granted',
    });
    setPushPromptPending(false);
    setShowPushOptIn(false);
    if (result.ok) {
      toast.success('Notificações ativadas neste navegador.');
    } else if (result.reason === 'sync_failed') {
      toast.error('Permissão ok, mas falhou ao registrar no servidor.');
    } else {
      toast.error('Não foi possível ativar as notificações.');
    }
  };

  const handlePushDismiss = () => {
    setPushPromptStatus('dismissed');
    setPushPromptPending(false);
    setShowPushOptIn(false);
  };

  return (
    <>
      {isAuthenticated && <Header />}
      <main style={{ minHeight: isAuthenticated ? 'calc(100vh - 140px)' : '100vh' }}>
        {children}
      </main>
      {isAuthenticated && <Footer />}
      <PushOptInModal
        isOpen={showPushOptIn}
        onAccept={handlePushAccept}
        onDismiss={handlePushDismiss}
      />
      <AvisoHost />
    </>
  );
};

function App() {

  const [maintenanceConfig, setMaintenanceConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showEnvironmentNotice, setShowEnvironmentNotice] = useState(false);

  // Configurar barras do sistema (Capacitor 8 / Android 16 edge-to-edge)
  useEffect(() => {
    const configureStatusBar = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const platform = Capacitor.getPlatform();
          // Conteúdo claro (ícones brancos) sobre o header azul
          await SystemBars.setStyle({ style: SystemBarsStyle.Dark });
          await StatusBar.setStyle({ style: Style.Dark });

          if (platform === 'android') {
            // Em Android <= 14 ainda aplica cor de fundo; em 15+ edge-to-edge prevalece
            try {
              await StatusBar.setBackgroundColor({ color: '#1e40af' });
            } catch (_) {
              // Ignora se a API não estiver disponível no dispositivo
            }
          }
        } catch (error) {
          console.log('StatusBar/SystemBars não disponível:', error);
        }
      }
    };
    configureStatusBar();
  }, []);

  useEffect(() => {
    const fetchMaintenanceConfig = async () => {
      try {
        const { maintenanceAPI } = await import('./services/api');
        const response = await maintenanceAPI.getConfig();
        const config = response.data;
        setMaintenanceConfig(config);
        setShowEnvironmentNotice(!!config?.environmentNotice?.enabled);
      } catch (err) {
        console.error("Falha na verificação de manutenção:", err);
        // Se der erro, assumimos que não está em manutenção
        setMaintenanceConfig({ isMaintenanceMode: false });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaintenanceConfig();
  }, []);

  // Detectar mudanças no tamanho da tela para ajustar posição do toast
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Se o modo de manutenção estiver ativo, renderiza a página de manutenção
  if (maintenanceConfig?.isMaintenanceMode) {
    return <MaintenancePage config={maintenanceConfig} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
        <AuthProvider>
          <ModalGateProvider>
          <Router>
            <AppLayout>
              <Routes>
                {/* Rotas públicas */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />

                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <Register />
                    </PublicRoute>
                  }
                />

                {/* Rotas protegidas */}
                <Route path="/contracts"
                  element={
                    <ProtectedRoute>
                      <Contrato />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dependents"
                  element={
                    <ProtectedRoute>
                      <Dependents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payments"
                  element={
                    <ProtectedRoute>
                      <Payments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/coverage"
                  element={
                    <ProtectedRoute>
                      <Coverage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/solicitacoes"
                  element={
                    <ProtectedRoute>
                      <Solicitacoes />
                    </ProtectedRoute>
                  }
                />

                {/* Rota padrão */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* Rota 404 */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AppLayout>
          </Router>

          <HomologationModal
            isOpen={showEnvironmentNotice}
            onClose={() => setShowEnvironmentNotice(false)}
            title={maintenanceConfig?.environmentNotice?.title || 'Ambiente de Homologação'}
            message={maintenanceConfig?.environmentNotice?.message || 'Você está acessando o ambiente de testes (homologação).'}
          />

          {/* Toast notifications */}
          <ToastContainer
            position={isMobile ? "bottom-center" : "top-right"}
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          </ModalGateProvider>
        </AuthProvider>
        )}
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;


